'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '@/shadcn/lib/utils';

interface Props {
    min: number;
    max: number;
    valueFrom: number;
    valueTo: number;
    step?: number;
    onChange: (from: number, to: number) => void;
    /** Volitelny formatter pre aria-valuetext (napr. cena s €). */
    format?: (n: number) => string;
    ariaLabelFrom?: string;
    ariaLabelTo?: string;
    className?: string | string[];
}

type Thumb = 'from' | 'to';

/**
 * Dvojuchytovy range slider - vlastny, bez externej zavislosti.
 * Zlata aktivna cast (brand), biele uchyty so zlatym prstencom, klavesnica
 * (sipky / Home / End), ARIA slider roly. Riadeny cez valueFrom/valueTo.
 */
export function RangeSlider(props: Props) {
    const { min, max, valueFrom, valueTo, onChange } = props;
    const step = props.step && props.step > 0 ? props.step : 1;
    const railRef = useRef<HTMLDivElement | null>(null);
    const draggingRef = useRef<Thumb | null>(null);
    const [dragging, setDragging] = useState<Thumb | null>(null);

    const span = Math.max(1, max - min);
    const clamp = (n: number) => Math.min(max, Math.max(min, n));
    const snap = (n: number) => clamp(Math.round((n - min) / step) * step + min);
    const pct = (n: number) => ((clamp(n) - min) / span) * 100;

    const fromPct = pct(valueFrom);
    const toPct = pct(valueTo);

    const setFrom = useCallback(
        (n: number) => onChange(Math.min(snap(n), valueTo - step), valueTo),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [onChange, valueTo, step, min, max],
    );
    const setTo = useCallback(
        (n: number) => onChange(valueFrom, Math.max(snap(n), valueFrom + step)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [onChange, valueFrom, step, min, max],
    );

    const valueFromClientX = useCallback(
        (clientX: number) => {
            const rail = railRef.current;
            if (!rail) return min;
            const rect = rail.getBoundingClientRect();
            const ratio = rect.width ? (clientX - rect.left) / rect.width : 0;
            return snap(min + Math.min(1, Math.max(0, ratio)) * span);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [min, max, step],
    );

    // Globalne pointer listenery pocas tahania (aj mimo uchytu).
    useEffect(() => {
        if (!dragging) return;
        const onMove = (e: PointerEvent) => {
            const v = valueFromClientX(e.clientX);
            if (draggingRef.current === 'from') setFrom(v);
            else if (draggingRef.current === 'to') setTo(v);
        };
        const onUp = () => {
            draggingRef.current = null;
            setDragging(null);
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onUp);
        return () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            window.removeEventListener('pointercancel', onUp);
        };
    }, [dragging, valueFromClientX, setFrom, setTo]);

    const startDrag = (thumb: Thumb) => (e: React.PointerEvent) => {
        e.preventDefault();
        draggingRef.current = thumb;
        setDragging(thumb);
    };

    const onKey = (thumb: Thumb) => (e: React.KeyboardEvent) => {
        const cur = thumb === 'from' ? valueFrom : valueTo;
        let next: number | null = null;
        switch (e.key) {
            case 'ArrowLeft':
            case 'ArrowDown':
                next = cur - step;
                break;
            case 'ArrowRight':
            case 'ArrowUp':
                next = cur + step;
                break;
            case 'PageDown':
                next = cur - step * 10;
                break;
            case 'PageUp':
                next = cur + step * 10;
                break;
            case 'Home':
                next = min;
                break;
            case 'End':
                next = max;
                break;
            default:
                return;
        }
        e.preventDefault();
        if (thumb === 'from') setFrom(next);
        else setTo(next);
    };

    const thumbBase = cn(
        'absolute top-1/2 z-[2] size-[20px] -translate-x-1/2 -translate-y-1/2 rounded-full cursor-grab',
        'bg-white border border-black/10 shadow-[0_3px_10px_rgba(0,0,0,.18)]',
        'ring-[3px] ring-[#f3c300]/25 outline-none',
        'transition-[box-shadow,ring] duration-200 ease-[var(--ease-rx)]',
        'focus-visible:ring-[#f3c300]/55 focus-visible:shadow-[0_4px_14px_rgba(201,143,0,.35)]',
        'active:cursor-grabbing',
    );

    const ariaText = (n: number) => (props.format ? props.format(n) : String(n));

    return (
        <div className={cn('w-full select-none px-[10px] py-[14px]', props.className)}>
            <div ref={railRef} className="relative h-[20px] w-full">
                {/* rail */}
                <div className="absolute left-0 top-1/2 h-[4px] w-full -translate-y-1/2 rounded-full bg-black/[.10]" />
                {/* aktivny rozsah */}
                <div
                    className="absolute top-1/2 h-[4px] -translate-y-1/2 rounded-full bg-cust-primary"
                    style={{ left: `${fromPct}%`, right: `${100 - toPct}%` }}
                />
                {/* from thumb */}
                <div
                    role="slider"
                    tabIndex={0}
                    aria-label={props.ariaLabelFrom ?? 'Od'}
                    aria-valuemin={min}
                    aria-valuemax={valueTo - step}
                    aria-valuenow={valueFrom}
                    aria-valuetext={ariaText(valueFrom)}
                    className={cn(thumbBase, dragging === 'from' && 'cursor-grabbing')}
                    style={{ left: `${fromPct}%` }}
                    onPointerDown={startDrag('from')}
                    onKeyDown={onKey('from')}
                />
                {/* to thumb */}
                <div
                    role="slider"
                    tabIndex={0}
                    aria-label={props.ariaLabelTo ?? 'Do'}
                    aria-valuemin={valueFrom + step}
                    aria-valuemax={max}
                    aria-valuenow={valueTo}
                    aria-valuetext={ariaText(valueTo)}
                    className={cn(thumbBase, dragging === 'to' && 'cursor-grabbing')}
                    style={{ left: `${toPct}%` }}
                    onPointerDown={startDrag('to')}
                    onKeyDown={onKey('to')}
                />
            </div>
        </div>
    );
}
